import { error } from '@reverbdotcom/commons/src/elog';

import type { IUser, IUserContext, Experiment } from '@reverbdotcom/commons/src/components/user_context_provider';
import type { ElogConfig } from '@reverbdotcom/commons/src/elog';
import type { UserContext as ElogUserContext } from '@reverbdotcom/commons/src/schemas/analytics';

const cache = {};

/*
 * parseMeta is useful for grabbing data out of the head
 * passed up from the server.
 *
 * parseMeta will _first_ check to see if the data is cached and
 * return that value first.
 *
 * while it may be tempting to store base64 tags into the head,
 * there are some serious issues with parsing utf8 strings.
 */
export function parseMeta<ParsedMetaType = any>(tagName: string): ParsedMetaType | null {
  if (cache[tagName]) {
    return cache[tagName];
  }

  const tag = document.querySelector<HTMLMetaElement>(`meta[name=${tagName}]`);

  try {
    if (!tag || !tag.content) {
      return null;
    }

    const parsed = JSON.parse(tag.content) as ParsedMetaType;
    cache[tagName] = parsed;

    return parsed;
  } catch (e) {
    error('parse_meta.error', {
      tagName,
      tagContent: tag.content,
      error: e,
    });
  }

  return null;
}

export type FrontendUserContext = {
  user: Omit<IUser, 'experiments'> & Pick<ElogUserContext, 'experiments'>;
};

/**
 * This "frontendCurrentUser" is what gets passed into the reverb-ui
 * React component tree as the current user context value (`IUserContext`)
 * for the `useUser()` hook and the `withUserContext` HOC.
 *
 * There is no official schema definition, but `IUserContext` is used throughout frontend.
 * The Reverb Core code that generates this value (except the `experiments` field) is defined here:
 * https://github.com/search?q=repo%3Areverbdotcom%2Freverb%20%22def%20frontend_user_config%22&type=code
 *
 * Because the `experiments` field size is large, it is only rendered once in the "elog-config"
 * meta tag content, instead of being duplicated in the "current-user" meta tag.
 * The Reverb core code that generates the "elog-config" meta tag is here:
 * https://github.com/search?q=repo%3Areverbdotcom%2Freverb+%22def+elog_config%22&type=code
 */
export function frontendCurrentUser(): IUserContext | null {
  const currentUserContext = parseMeta<IUserContext>('current-user');
  const elogConfig = parseMeta<ElogConfig>('elog-config');

  // If either of these meta tags cannot be parsed, something very strange (probably exceptional) happened.
  // Returning null is also a strange way to proceed in this situation, but this is legacy code.
  if (!elogConfig || !currentUserContext) {
    return null;
  }

  // elog uses protobuf schemas where all properties are optional,
  // but all experiment properties are always provided, so type coercion is safe.
  const experiments = elogConfig.elog.userContext.experiments as Experiment[];

  return {
    user: {
      ...currentUserContext.user,
      experiments,
    },
  };
}
